var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { env } from '../environment';
import style from '../css/icon_css';
let Icon = class Icon extends LitElement {
    constructor() {
        super(...arguments);
        this.disabled = undefined;
        this.cursor = undefined;
        this.version = '';
        this.family = 'fas';
        this.type = 'fa-barcode';
        this.dt_invert = false;
        this.dt_first_color = 'currentColor';
        this.dt_second_color = 'currentColor';
        this.dt_first_opacity = 'inherit';
        this.dt_second_opacity = 'inherit';
    }
    static get styles() {
        if (env.debug) {
            return css ``;
        }
        return [style];
    }
    // Custom kit icons (fa-kit / fa-kit-duotone) don't penetrate shadow DOM —
    // render to light DOM so the FA kit CSS applies directly.
    createRenderRoot() {
        const family = this.getAttribute('family');
        if (family === 'fa-kit' || family === 'fa-kit-duotone') {
            return this;
        }
        return super.createRenderRoot();
    }
    updated() {
        this.loadCSSIfNotAlreadyLoaded();
    }
    loadCSSIfNotAlreadyLoaded() {
        const fa_src = 'https://kit.fontawesome.com/352997b648.js';
        const fa_script = document.createElement('script');
        fa_script.src = fa_src;
        fa_script.crossOrigin = 'anonymous';
        if (!document.head.querySelector(`script[src='${fa_src}']`)) {
            document.getElementsByTagName('head')[0].appendChild(fa_script);
        }
    }
    // Light-DOM kit icons can't see shadow styles. Inject scoped button styles
    // into document.head once so elevated/floating versions work for kit icons too.
    // Hex values are hardcoded because --gs1-color-* CSS vars live on :host only.
    loadKitBtnCSSIfNotAlreadyLoaded() {
        const marker = 'data-gs1-fa-icon-kit-btn';
        if (document.head.querySelector(`style[${marker}]`))
            return;
        const styleEl = document.createElement('style');
        styleEl.setAttribute(marker, '');
        styleEl.textContent = `
      gs1-fa-icon i[disabled=disabled],
      gs1-fa-icon svg[disabled=disabled] {
        color: #a6b0bb !important;
      }
      gs1-fa-icon .btn-icon {
        font-size: inherit;
        line-height: 2em;
        width: 2em;
        height: 2em;
        text-align: center;
        color: currentColor;
        padding: 0;
      }
      gs1-fa-icon .btn-icon.btn-icon-elevated:active,
      gs1-fa-icon .btn-icon.btn-icon-elevated:focus,
      gs1-fa-icon .btn-icon.btn-icon-elevated:focus-visible,
      gs1-fa-icon .btn-icon.btn-icon-floating:active,
      gs1-fa-icon .btn-icon.btn-icon-floating:focus,
      gs1-fa-icon .btn-icon.btn-icon-floating:focus-visible {
        outline: solid 2px #002c6c;
      }
      gs1-fa-icon .btn-icon-elevated {
        box-shadow: 0 2px 3px 0 rgba(0, 44, 108, 0.2);
        border: solid 1px #e5f0fc;
        border-radius: 50%;
        background-color: #ffffff;
        transition: transform 0.3s;
      }
      gs1-fa-icon .btn-icon-elevated:hover:not(:disabled) {
        transform: translateY(-4px);
        box-shadow: 0 10px 30px 0 rgba(0, 44, 108, 0.2);
      }
      gs1-fa-icon .btn-icon-elevated:disabled {
        outline: none !important;
      }
      gs1-fa-icon .btn-icon-elevated:disabled i,
      gs1-fa-icon .btn-icon-elevated:disabled svg {
        color: #a6b0bb;
      }
      gs1-fa-icon .btn-icon-floating {
        border: 0;
        border-radius: 0.25rem;
        background-color: #ffffff;
        transition: transform 0.3s;
      }
      gs1-fa-icon .btn-icon-floating:hover:not(:disabled) {
        background-color: #e5f0fc;
      }
      gs1-fa-icon .btn-icon-floating:disabled {
        background-color: #e5e8eb;
        outline: none !important;
      }
    `;
        document.head.appendChild(styleEl);
    }
    render() {
        const isDuotoneFamily = this.family == 'fad' || this.family == 'fa-kit-duotone';
        let dt_swap_opacity = '';
        if (isDuotoneFamily && this.dt_invert) {
            dt_swap_opacity = 'fa-swap-opacity';
        }
        const isDuotoneActive = isDuotoneFamily && !this.disabled;
        const styles = {
            '--fa-primary-color': isDuotoneActive
                ? this.dt_first_color || 'currentColor'
                : 'currentColor',
            '--fa-secondary-color': isDuotoneActive
                ? this.dt_second_color || 'currentColor'
                : 'currentColor',
            '--fa-primary-opacity': isDuotoneActive ? this.dt_first_opacity : 'inherit',
            '--fa-secondary-opacity': isDuotoneActive
                ? this.dt_second_opacity
                : 'inherit',
            cursor: this.cursor,
        };
        const iconEl = html `<i
      part="ico"
      class="${this.family} ${this.type} ${dt_swap_opacity}"
      disabled="${ifDefined(this.disabled)}"
      style=${styleMap(styles)}
    ></i>`;
        const isKitFamily = this.family === 'fa-kit' || this.family === 'fa-kit-duotone';
        const hasBtnWrapper = this.version == 'elevated' || this.version == 'floating';
        if (isKitFamily) {
            this.loadKitBtnCSSIfNotAlreadyLoaded();
            if (hasBtnWrapper) {
                return html `<button
          class="btn-icon btn-icon-${this.version}"
          ?disabled=${this.disabled}
        >
          ${iconEl}
        </button>`;
            }
            return iconEl;
        }
        return html ` ${env.debug
            ? html `<link rel="stylesheet" href="${env.baseUrl}/css/icon.css" />`
            : html ``}
    ${hasBtnWrapper
            ? html `<button
          class="btn-icon btn-icon-${this.version}"
          ?disabled=${this.disabled}
        >
          ${iconEl}
        </button>`
            : iconEl}`;
    }
};
__decorate([
    property({ reflect: true })
], Icon.prototype, "disabled", void 0);
__decorate([
    property()
], Icon.prototype, "cursor", void 0);
__decorate([
    property()
], Icon.prototype, "version", void 0);
__decorate([
    property()
], Icon.prototype, "family", void 0);
__decorate([
    property()
], Icon.prototype, "type", void 0);
__decorate([
    property()
], Icon.prototype, "dt_invert", void 0);
__decorate([
    property()
], Icon.prototype, "dt_first_color", void 0);
__decorate([
    property()
], Icon.prototype, "dt_second_color", void 0);
__decorate([
    property()
], Icon.prototype, "dt_first_opacity", void 0);
__decorate([
    property()
], Icon.prototype, "dt_second_opacity", void 0);
Icon = __decorate([
    customElement('gs1-fa-icon')
], Icon);
export { Icon };
//# sourceMappingURL=icon.js.map